import { useState } from "react";
import type {
  CmmdDeviceRegistryDevice,
  CmmdDeviceRegistryRuntime,
  EnvironmentId,
} from "@cmmd-forge/contracts";

import { cmmdDeviceRegistryResource } from "../../cmmd/cmmdDeviceRegistryResource";
import { cmmdRuntimeInventoryResource } from "../../cmmd/cmmdRuntimeInventoryResource";
import { cmmdApiClient } from "../../environments/cmmdApi";
import { removeSavedEnvironment } from "../../environments/runtime/service";
import { toastManager } from "../ui/toast";
import { useDestructiveConfirm, type DestructiveConfirmHandle } from "./useDestructiveConfirm";

type DeviceDisconnectState =
  | { readonly status: "idle" }
  | { readonly status: "disconnecting"; readonly deviceId: number };

export interface SidebarDeviceDisconnectController {
  readonly disconnectingDeviceId: number | null;
  readonly disconnectedRuntimeIds: ReadonlySet<EnvironmentId>;
  readonly DestructiveConfirmDialog: DestructiveConfirmHandle["DestructiveConfirmDialog"];
  readonly disconnectDevice: (
    device: CmmdDeviceRegistryDevice,
    runtimeId: EnvironmentId,
  ) => Promise<void>;
}

function resolveDeviceRuntimeIds(input: {
  readonly deviceIds: ReadonlySet<number>;
  readonly registeredRuntimes: readonly CmmdDeviceRegistryRuntime[];
  readonly fallbackRuntimeId: EnvironmentId;
}): ReadonlySet<EnvironmentId> {
  const runtimeIds = new Set(
    input.registeredRuntimes
      .filter((runtime) => runtime.deviceId !== null && input.deviceIds.has(runtime.deviceId))
      .map((runtime) => runtime.id as EnvironmentId),
  );
  runtimeIds.add(input.fallbackRuntimeId);
  return runtimeIds;
}

function sharesPhysicalHost(
  candidate: CmmdDeviceRegistryDevice,
  target: CmmdDeviceRegistryDevice,
): boolean {
  if (candidate.id === target.id) return true;
  if (candidate.deviceType !== "desktop" || target.deviceType !== "desktop") return false;
  if (candidate.hostId !== null && target.hostId !== null) {
    return candidate.hostId === target.hostId;
  }
  const candidateHostKey = candidate.hostKey?.trim() ?? "";
  const targetHostKey = target.hostKey?.trim() ?? "";
  return candidateHostKey.length > 0 && candidateHostKey === targetHostKey;
}

function resolveRevocableDesktopConnections(input: {
  readonly target: CmmdDeviceRegistryDevice;
  readonly registeredDevices: readonly CmmdDeviceRegistryDevice[];
}): readonly CmmdDeviceRegistryDevice[] {
  const devicesById = new Map(
    [...input.registeredDevices, input.target].map((device) => [device.id, device]),
  );
  return [...devicesById.values()].filter(
    (candidate) =>
      sharesPhysicalHost(candidate, input.target) &&
      candidate.trustState !== "revoked" &&
      candidate.authorization.bound &&
      candidate.authorization.revoked !== true,
  );
}

function reportMissingAuthorization(device: CmmdDeviceRegistryDevice): void {
  toastManager.add({
    type: "error",
    title: "Could not disconnect device",
    description: `${device.displayName} has no CMMD authorization to revoke.`,
  });
}

async function shouldDisconnectDevice(
  state: DeviceDisconnectState,
  device: CmmdDeviceRegistryDevice,
  confirm: DestructiveConfirmHandle["confirm"],
): Promise<boolean> {
  if (state.status === "disconnecting") return false;
  if (!device.authorization.bound) {
    reportMissingAuthorization(device);
    return false;
  }
  return confirm({
    title: `Disconnect ${device.displayName}?`,
    description:
      "All Forge desktop connections for this physical device will be revoked, and its projects and threads will be removed from this sidebar. The device must sign in again before it can reconnect to Forge or CMMD.",
    confirmLabel: "Disconnect",
  });
}

async function revokeRegisteredDevice(
  device: CmmdDeviceRegistryDevice,
  fallbackRuntimeId: EnvironmentId,
): Promise<ReadonlySet<EnvironmentId>> {
  const registry = cmmdDeviceRegistryResource.read().registry;
  const targetDevices = resolveRevocableDesktopConnections({
    target: device,
    registeredDevices: registry?.devices ?? [],
  });
  const runtimeIds = resolveDeviceRuntimeIds({
    deviceIds: new Set(targetDevices.map((targetDevice) => targetDevice.id)),
    registeredRuntimes: registry?.runtimes ?? [],
    fallbackRuntimeId,
  });
  const revokedDevices = await Promise.all(
    targetDevices.map((targetDevice) => cmmdApiClient.revokeDevice(targetDevice.id)),
  );
  for (const revokedDevice of revokedDevices) {
    cmmdDeviceRegistryResource.publishDeviceRevocation(revokedDevice);
  }
  return runtimeIds;
}

async function synchronizeDisconnectedRuntimes(
  runtimeIds: ReadonlySet<EnvironmentId>,
): Promise<number> {
  const results = await Promise.allSettled([
    ...[...runtimeIds].map((runtimeId) => removeSavedEnvironment(runtimeId)),
    cmmdDeviceRegistryResource.refresh(),
    cmmdRuntimeInventoryResource.refresh({ allWorkspaces: true }),
  ]);
  return results.filter((result) => result.status === "rejected").length;
}

function reportDisconnectSuccess(device: CmmdDeviceRegistryDevice, failedRefreshes: number): void {
  toastManager.add({
    type: failedRefreshes === 0 ? "success" : "warning",
    title: "Device disconnected",
    description:
      failedRefreshes === 0
        ? `${device.displayName} was signed out and removed from the sidebar.`
        : `${device.displayName} was signed out. Refresh Forge if stale projects remain visible.`,
  });
}

function reportDisconnectError(error: unknown): void {
  toastManager.add({
    type: "error",
    title: "Could not disconnect device",
    description: error instanceof Error ? error.message : "Could not disconnect device.",
  });
}

export function useSidebarDeviceDisconnect(): SidebarDeviceDisconnectController {
  const destructiveConfirm = useDestructiveConfirm();
  const [state, setState] = useState<DeviceDisconnectState>({ status: "idle" });
  const [disconnectedRuntimeIds, setDisconnectedRuntimeIds] = useState<ReadonlySet<EnvironmentId>>(
    new Set(),
  );

  async function disconnectDevice(
    device: CmmdDeviceRegistryDevice,
    runtimeId: EnvironmentId,
  ): Promise<void> {
    if (!(await shouldDisconnectDevice(state, device, destructiveConfirm.confirm))) return;

    setState({ status: "disconnecting", deviceId: device.id });
    try {
      const runtimeIds = await revokeRegisteredDevice(device, runtimeId);
      setDisconnectedRuntimeIds((current) => new Set([...current, ...runtimeIds]));
      reportDisconnectSuccess(device, await synchronizeDisconnectedRuntimes(runtimeIds));
    } catch (error) {
      reportDisconnectError(error);
    } finally {
      setState({ status: "idle" });
    }
  }

  return {
    disconnectingDeviceId: state.status === "disconnecting" ? state.deviceId : null,
    disconnectedRuntimeIds,
    DestructiveConfirmDialog: destructiveConfirm.DestructiveConfirmDialog,
    disconnectDevice,
  };
}
